import {
    NavigationBar,
    IconChevronLeftRegular,
    Text2,
    ThemeVariant,
    Tag,
    BoxedRow,
    EmptyState,
    IconCheckedRegular,
    IconTimeRegular,
    IconFolderRegular,
    IconCancelRegular,
    Spinner,
    skinVars,
    IconInformationUserRegular,
} from '@telefonica/mistica';
import { AxiosError, AxiosResponse } from 'axios';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getListProblems } from '../../../services/ProblemService';
import { getFormatDate } from '../../../utils/dataTransformation/dateConversions';
import navigateParams from '../../../utils/navigation/navigateParams';
import './index.scss';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import { managementEnvironment } from '../../../utils/dataTransformation/environments';
import Stopper from '../../../components/Stopper';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

export const Incidents: React.FC = () => {
    const navigate = useNavigate();
    const supportDisabled = managementEnvironment().REACT_APP_STOPPER_PROBLEMS_DISABLED;
    const [problems, setProblems] = useState([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { trackingScreenView } = new TrackingEvents();

    const sendToNative = (url: string, type: string, title: string, id?: string) => {
        const queryParams = { title, id };
        const urlComposed = url + '/' + id;
        if (navigateParams(type, urlComposed, queryParams, {}, title)) {
            id ? navigate(urlComposed) : navigate(url);
        }
    };

    const setTypeTagPhase = (phase: any, situation: any) => {
        if (phase === 'ABI') {
            return (
                <Tag Icon={IconTimeRegular} type='warning'>
                    {t('ordersTagInProgress')}
                </Tag>
            );
        } else if (phase === 'CUR') {
            return situation === 'Pendiente de diagnóstico de cliente' ? (
                <Tag Icon={IconInformationUserRegular} type='error'>
                    {t('ordersTagPending')}
                </Tag>
            ) : (
                <Tag Icon={IconTimeRegular} type='warning'>
                    {t('ordersTagInProgress')}
                </Tag>
            );
        } else if (phase === 'CON') {
            return (
                <Tag Icon={IconCheckedRegular} type='success'>
                    {t('supportTagIncidentsResolved')}
                </Tag>
            );
        } else if (phase === 'PEN') {
            return (
                <Tag Icon={IconFolderRegular} type='active'>
                    {t('supportTagIncidentsFinished')}
                </Tag>
            );
        } else if (phase === 'CER') {
            return situation === 'Cancelado' || situation === 'Anulado' ? (
                <Tag Icon={IconCancelRegular} type='inactive'>
                    {t('supportTagIncidentsCancelled')}
                </Tag>
            ) : (
                <Tag Icon={IconCancelRegular} type='inactive'>
                    {t('supportTagIncidentsClosed')}
                </Tag>
            );
        }
        return null;
    };

    useEffect(() => {
        getListProblems()
            .then((response: AxiosResponse) => {
                setProblems(response.data);
                setLoading(false);
            })
            .catch((error: AxiosError) => {
                console.error(error);
                setLoading(false);
            });

        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    if (loading) {
        return (
            <div className='c-spinner'>
                <Spinner size={24} />
                <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
            </div>
        );
    }
    return supportDisabled ? (
        <Stopper></Stopper>
    ) : (
        <div className='incidents-page'>
            <div className='incidents-page__header' style={{ background: skinVars.colors.backgroundBrand }}>
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <>
                        <div className='incidents-page__header-navigationBar'>
                            <NavigationBar
                                isInverse={true}
                                onBack={() => {
                                    navigate('/support');
                                }}
                                title={t('returnSupportIncidents')}
                            />
                        </div>
                        <div className='incidents-page__header-return'>
                            <div
                                className='incidents-page__header-return-cursor'
                                onClick={() => {
                                    navigate('/support');
                                }}
                            >
                                <div className='incidents-page__header-return-cursor-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.appBarBackground} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.appBarBackground}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </div>
                    </>
                )}
                <div className='incidents-page__header-breadcrumbs'>
                    <ThemeVariant isInverse>
                        <BreadCrumbsWrapper
                            title={t('returnSupportIncidents')}
                            breadcrumbs={[{ title: t('breadcrumbsSupport'), url: '/support' }]}
                        />
                    </ThemeVariant>
                </div>
            </div>
            <div className='incidents-page__content'>
                {problems.length > 0 ? (
                    problems?.map((item: any, index: number) => {
                        return (
                            <div className='incidents-page__content-card' key={index}>
                                <BoxedRow
                                    headline={setTypeTagPhase(item.phase, item.situation)}
                                    title={item.motive.description}
                                    description={t('supportIncidentNumber') + ` ${item.code}`}
                                    extra={
                                        <div className='incidents-page__content-card-extra'>
                                            <div className='incidents-page__content-card-extra-startDate'>
                                                <div className='incidents-page__content-card-extra-startDate-title'>
                                                    <Text2 regular color={skinVars.colors.textPrimary}>
                                                        {t('supportIncidentsCreationDate')}
                                                    </Text2>
                                                </div>
                                                <Text2 regular color={skinVars.colors.textSecondary}>
                                                    {getFormatDate(new Date(item.createdAt as any), 'DD/MM/YYYY')}
                                                </Text2>
                                            </div>
                                            {item.endedAt && (
                                                <div className='incidents-page__content-card-extra-finishDate'>
                                                    <div className='incidents-page__content-card-extra-finishDate-title'>
                                                        <Text2 regular color={skinVars.colors.textPrimary}>
                                                            {t('supportIncidentsEndDate')}
                                                        </Text2>
                                                    </div>
                                                    <Text2 regular color={skinVars.colors.textSecondary}>
                                                        {getFormatDate(new Date(item.endedAt as any), 'DD/MM/YYYY')}
                                                    </Text2>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    onPress={() =>
                                        sendToNative(
                                            '/support/incidents/incident-detail',
                                            'modeFlow-backgroundBlue',
                                            t('returnsupportIncidentDetail'),
                                            item.id,
                                        )
                                    }
                                ></BoxedRow>
                            </div>
                        );
                    })
                ) : (
                    <div className='incidents-page__content-emptyState'>
                        <EmptyState
                            icon={<IconInformationUserRegular color={skinVars.colors.brand} size={64} />}
                            title={t('supportEmptyStateNoIncidentTitle')}
                            description={t('supportEmptyStateNoIncidentDescription')}
                        ></EmptyState>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Incidents;
